import { useCallback } from "react";

import { useGlobalPayments } from "./GlobalPaymentsContext";

const usePaymentMethodProviders = () => {
  const { state, dispatch } = useGlobalPayments();
  const paymentMethodProviders = state?.paymentMethodProviders;

  const setPaymentMethodProviders = useCallback(
    (providers) => {
      dispatch({ type: "setPaymentMethodProviders", payload: providers });
    },
    [dispatch]
  );

  return { paymentMethodProviders, setPaymentMethodProviders };
};

const getProvider = (paymentProvider) => {
  let providerIsStax;
  let providerIsWePay;

  if (typeof paymentProvider === "number") {
    providerIsWePay = paymentProvider === 1;
    providerIsStax = paymentProvider === 2;
  } else {
    providerIsStax = paymentProvider === "stax";
    providerIsWePay = paymentProvider === "wepay";
  }

  return { providerIsStax, providerIsWePay };
};

const getProviderName = (paymentProvider) => {
  if (typeof paymentProvider === "number") {
    return paymentProvider === 1 ? "wepay" : "stax";
  }

  return paymentProvider;
};

const useCardProvider = () => {
  const { state } = useGlobalPayments();
  const paymentMethodProviders = state?.paymentMethodProviders;

  const card = paymentMethodProviders?.find(
    (method) => method.paymentMethodCategory === "credit_card"
  );
  const provider = getProviderName(card?.paymentProvider);
  const { providerIsStax, providerIsWePay } = getProvider(provider);

  return { provider, providerIsStax, providerIsWePay };
};

const useBankProvider = () => {
  const { state } = useGlobalPayments();
  const paymentMethodProviders = state?.paymentMethodProviders;
  const bank = paymentMethodProviders?.find(
    (method) => method.paymentMethodCategory === "bank_account"
  );
  const provider = getProviderName(bank?.paymentProvider);
  const { providerIsStax, providerIsWePay } = getProvider(provider);

  return { provider, providerIsStax, providerIsWePay };
};

const useMerchantToken = () => {
  const { state } = useGlobalPayments();

  const merchantTokens = state?.paymentMethodProviders
    ?.filter((provider) => provider?.merchantToken)
    ?.map((provider) => provider?.merchantToken);

  // merchant tokens should be the same for all Stax payment method providers b/c each network should only have 1 merchant token
  const merchantToken = merchantTokens?.length ? merchantTokens[0] : "";

  return merchantToken;
};

const useFetching = () => {
  const { state, setState } = useGlobalPayments();
  const { fetching, fetchComplete } = state;

  const setFetching = useCallback(
    (val) => {
      setState({ fetching: val });
    },
    [setState]
  );

  const setFetchCompleted = useCallback(
    (val) => {
      setState({ fetchComplete: val });
    },
    [setState]
  );

  return { fetching, fetchComplete, setFetching, setFetchCompleted };
};

export {
  usePaymentMethodProviders,
  useBankProvider,
  useCardProvider,
  useMerchantToken,
  getProviderName,
  useFetching,
};
